import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Loader2, X } from 'lucide-react';
import { Badge } from '../ui';
import { orderErrorMessage } from './orderError';
import { fmtPrice, fmtQty, statusConfig } from './orderPresentation';
import { ordersService } from '../../services/orders.service';
import { portfolioService } from '../../services/portfolio.service';
import { customersService } from '../../services/customers.service';
import { balancesFromPortfolio, portfolioIdCandidates, resolveCustomerPortfolio } from '../portfolio/portfolioModel';
import CustomerPicker from '../financials/CustomerPicker';
import AssetPicker from './AssetPicker';
import AssetLiveQuote from './AssetLiveQuote';

const ORDER_TYPES = ['MARKET', 'LIMIT'];
const EXPIRATION_OPTIONS = ['DAY', 'GTC'];
const EXPIRATION_LABELS = { DAY: 'Day', GTC: 'Good till cancelled' };

const INITIAL_FORM = {
  customerId: '',
  symbol: '',
  side: 'BUY',
  type: 'MARKET',
  quantity: '',
  price: '',
  expiration: 'DAY',
};

const toNumber = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : undefined;
};

const fmtCash = (v, currency) =>
  v != null
    ? `${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? ` ${currency}` : ''}`
    : '—';

const walletsOf = (balances) => {
  if (Array.isArray(balances)) return balances;
  if (Array.isArray(balances?.balances)) return balances.balances;
  return [];
};

export const buyingPowerFrom = (balances) => {
  const wallets = walletsOf(balances);
  const primary = wallets[0];
  return {
    amount: primary?.availableForTrading ?? primary?.availableCash ?? primary?.cashBalance ?? balances?.totalAvailable ?? balances?.totalAvailableForTrading,
    currency: primary?.currency ?? primary?.currencySymbol ?? balances?.primaryCurrency ?? balances?.currencySymbol,
  };
};

const PlaceOrderModal = ({ onClose, onSuccess, initialCustomerId = '' }) => {
  const [step, setStep] = useState('form');
  const [form, setForm] = useState(() => ({ ...INITIAL_FORM, customerId: initialCustomerId }));
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [placeError, setPlaceError] = useState(null);
  const [placedOrder, setPlacedOrder] = useState(null);
  const [balances, setBalances] = useState(null);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [position, setPosition] = useState(undefined);
  const [positionLoading, setPositionLoading] = useState(false);
  const [initialCustomer, setInitialCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [portfolioCustomerId, setPortfolioCustomerId] = useState(initialCustomerId);
  const lookupKeyRef = useRef('');
  const requestRef = useRef(0);
  const idempotencyKey = useRef(window.crypto?.randomUUID?.() || `order-${Date.now()}`);

  useEffect(() => () => {
    requestRef.current += 1;
  }, []);

  useEffect(() => {
    if (!initialCustomerId) return undefined;
    let cancelled = false;
    customersService
      .getCustomer(initialCustomerId)
      .then((customer) => {
        if (!cancelled && customer) {
          setInitialCustomer(customer);
          setSelectedCustomer((current) => current || customer);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [initialCustomerId]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  useEffect(() => {
    if (form.side !== 'BUY') {
      lookupKeyRef.current = '';
      setBalances(null);
      return undefined;
    }

    const usingCustomer = Boolean(selectedCustomer);
    const lookupKey = usingCustomer
      ? `customer:${portfolioIdCandidates(selectedCustomer).join('|')}`
      : `manual:${form.customerId || ''}`;

    if (!lookupKey || lookupKey === 'customer:' || lookupKey === 'manual:') {
      lookupKeyRef.current = '';
      setBalances(null);
      setBalancesLoading(false);
      return undefined;
    }
    if (lookupKeyRef.current === lookupKey) return undefined;
    lookupKeyRef.current = lookupKey;

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setBalancesLoading(true);

    const apply = (result) => {
      if (requestRef.current !== requestId) return;
      setBalances(result);
      setBalancesLoading(false);
    };
    const fail = () => {
      if (requestRef.current !== requestId) return;
      setBalances(null);
      setBalancesLoading(false);
    };

    if (usingCustomer) {
      resolveCustomerPortfolio(
        (id, options) => portfolioService.getCustomerPortfolio(id, options),
        selectedCustomer,
      )
        .then(({ customerId, portfolio }) => {
          if (requestRef.current !== requestId) return;
          if (customerId) {
            setPortfolioCustomerId(customerId);
            setForm((current) => (
              current.customerId === customerId ? current : { ...current, customerId }
            ));
          }
          apply(balancesFromPortfolio(portfolio));
        })
        .catch(fail);
    } else {
      setPortfolioCustomerId(form.customerId);
      portfolioService.getBalances(form.customerId).then(apply).catch(fail);
    }
    return undefined;
  }, [form.customerId, form.side, selectedCustomer]);

  const orderCustomerId = portfolioCustomerId || form.customerId;

  useEffect(() => {
    if (!orderCustomerId || form.side !== 'SELL' || !form.symbol) {
      setPosition(undefined);
      return undefined;
    }
    let cancelled = false;
    setPositionLoading(true);
    portfolioService
      .getPositions(orderCustomerId)
      .then((result) => {
        if (cancelled) return;
        const list = Array.isArray(result?.positions) ? result.positions : [];
        const match = list.find((p) => p.symbol?.toUpperCase() === form.symbol.toUpperCase());
        setPosition(match || null);
      })
      .catch(() => {
        if (!cancelled) setPosition(null);
      })
      .finally(() => {
        if (!cancelled) setPositionLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderCustomerId, form.side, form.symbol]);

  const buyingPower = buyingPowerFrom(balances);
  const qty = toNumber(form.quantity);
  const limitPrice = toNumber(form.price);
  const refPrice = form.type === 'LIMIT' ? limitPrice : toNumber(selectedAsset?.price);
  const estimatedNotional = qty != null && qty > 0 && refPrice != null ? qty * refPrice : null;
  const canPreview = Boolean(orderCustomerId && form.symbol.trim() && qty != null && (!selectedCustomer || portfolioCustomerId));

  const buildPayload = () => ({
    customerId: orderCustomerId,
    symbol: form.symbol.trim().toUpperCase(),
    assetId: selectedAsset?.id || undefined,
    marketSlug: selectedAsset?.marketSlug || undefined,
    type: form.type,
    expiration: form.expiration,
    quantity: qty,
    price: form.type === 'LIMIT' ? limitPrice : undefined,
  });

  const handlePreview = async (e) => {
    e.preventDefault();
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      const payload = buildPayload();
      const result =
        form.side === 'BUY'
          ? await ordersService.previewBuy(payload)
          : await ordersService.previewSell(payload);
      setPreview(result);
      setStep('preview');
    } catch (err) {
      setPreviewError(orderErrorMessage(err, 'Preview failed. Check the details and try again.'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirm = async () => {
    setPlaceError(null);
    setStep('placing');
    try {
      const payload = buildPayload();
      const result = await (form.side === 'BUY'
        ? ordersService.placeBuy(payload, idempotencyKey.current)
        : ordersService.placeSell(payload, idempotencyKey.current));
      setPlacedOrder(result || null);
      if (result?.status === 'REJECTED' || result?.status === 'FAILED') {
        setPlaceError(
          (typeof result.rejectReason === 'string' && result.rejectReason.trim())
            || 'The order was rejected.',
        );
        setStep('rejected');
      } else {
        setStep('success');
      }
      onSuccess?.();
    } catch (err) {
      setPlaceError(orderErrorMessage(err, 'The order could not be placed. Review the details and try again.'));
      setStep('preview');
    }
  };

  const contextRows = useMemo(() => {
    if (!orderCustomerId) return [];
    if (form.side === 'BUY') {
      return [
        {
          label: 'Available cash',
          value: balancesLoading ? null : fmtCash(buyingPower.amount, buyingPower.currency),
          loading: balancesLoading,
        },
      ];
    }
    if (!form.symbol) return [];
    return [
      {
        label: `Held shares (${form.symbol.toUpperCase()})`,
        value: positionLoading
          ? null
          : position
            ? Number(position.availableQuantity ?? position.quantity).toLocaleString()
            : '0',
        loading: positionLoading,
      },
    ];
  }, [orderCustomerId, form.side, form.symbol, balancesLoading, buyingPower, position, positionLoading]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-900/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-start justify-between px-6 py-4 border-b border-ink-200 shrink-0">
          <div>
            <h2 className="text-base font-semibold text-ink-900">
              {step === 'success' ? 'Order submitted' : step === 'rejected' ? 'Order not placed' : 'Place order'}
            </h2>
            <p className="mt-0.5 text-xs text-ink-500">
              {step === 'form' || step === 'preview'
                ? 'Buy or sell on behalf of a customer. Preview the estimate, then confirm to place.'
                : 'The result below is what the order service returned.'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-ink-400 hover:text-ink-700" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {step === 'form' && (
          <form onSubmit={handlePreview} className="flex-1 overflow-y-auto">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-6 space-y-4 md:border-r border-ink-100">
                <div>
                  <label className="block text-xs font-medium text-ink-600 mb-1.5">Customer</label>
                  <CustomerPicker
                    value={form.customerId}
                    onChange={(id, customer) => {
                      lookupKeyRef.current = '';
                      setSelectedCustomer(customer || null);
                      setPortfolioCustomerId(customer ? '' : (id || ''));
                      setForm((f) => ({ ...f, customerId: id || '' }));
                    }}
                    placeholder="Search customer…"
                    initialSelected={initialCustomer}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-ink-600 mb-1.5">Asset</label>
                  <AssetPicker
                    value={form.symbol}
                    onChange={(symbol, asset) => {
                      setForm((f) => ({ ...f, symbol }));
                      setSelectedAsset(asset);
                    }}
                    placeholder="Search symbol or name…"
                  />
                </div>

                {selectedAsset?.id && (
                  <AssetLiveQuote assetId={selectedAsset.id} fallback={selectedAsset} chartHeightClass="h-32" />
                )}
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-ink-600 mb-1.5">Side</label>
                  <div className="flex gap-2">
                    {['BUY', 'SELL'].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, side: s }))}
                        className={`flex-1 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                          form.side === s
                            ? s === 'BUY'
                              ? 'bg-success-600 text-white border-success-600'
                              : 'bg-danger-600 text-white border-danger-600'
                            : 'border-ink-200 text-ink-600 hover:bg-ink-50'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-ink-600 mb-1.5">Order type</label>
                    <div className="flex gap-1 p-0.5 rounded-lg border border-ink-200 bg-ink-50">
                      {ORDER_TYPES.map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, type: t }))}
                          className={`flex-1 py-1.5 rounded-[5px] text-xs font-medium transition-colors ${
                            form.type === t ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-700'
                          }`}
                        >
                          {t.charAt(0) + t.slice(1).toLowerCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ink-600 mb-1.5">Quantity (shares)</label>
                    <input
                      className="input w-full"
                      type="number"
                      min="0"
                      step="any"
                      placeholder="e.g. 10"
                      value={form.quantity}
                      onChange={set('quantity')}
                      required
                    />
                  </div>
                </div>

                {form.type === 'LIMIT' && (
                  <div>
                    <label className="block text-xs font-medium text-ink-600 mb-1.5">Limit price</label>
                    <input
                      className="input w-full"
                      type="number"
                      min="0"
                      step="any"
                      placeholder="e.g. 150.00"
                      value={form.price}
                      onChange={set('price')}
                      required
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-ink-600 mb-1.5">Time in force</label>
                  <div className="flex gap-1 p-0.5 rounded-lg border border-ink-200 bg-ink-50">
                    {EXPIRATION_OPTIONS.map((exp) => (
                      <button
                        key={exp}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, expiration: exp }))}
                        className={`flex-1 py-1.5 rounded-[5px] text-xs font-medium transition-colors ${
                          form.expiration === exp ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-700'
                        }`}
                      >
                        {EXPIRATION_LABELS[exp]}
                      </button>
                    ))}
                  </div>
                </div>

                {(contextRows.length > 0 || estimatedNotional != null) && (
                  <div className="rounded-lg border border-ink-200 bg-ink-50 divide-y divide-ink-100 text-sm">
                    {contextRows.map((row) => (
                      <div key={row.label} className="flex items-center justify-between px-3 py-2">
                        <span className="text-ink-500">{row.label}</span>
                        {row.loading ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-ink-400" />
                        ) : (
                          <span className="font-mono font-medium text-ink-900 tnum">{row.value}</span>
                        )}
                      </div>
                    ))}
                    {estimatedNotional != null && (
                      <div className="flex items-center justify-between px-3 py-2">
                        <span className="text-ink-500">Est. notional</span>
                        <span className="font-mono font-medium text-ink-900 tnum">
                          {fmtCash(estimatedNotional, buyingPower.currency || selectedAsset?.currency)}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {previewError && (
                  <p className="text-sm text-danger-600 bg-danger-50 border border-danger-100 rounded-lg px-3 py-2">
                    {previewError}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-ink-200 bg-ink-50">
              <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
              </button>
              <button
                type="submit"
                disabled={previewLoading || !canPreview}
                className="btn-primary flex items-center gap-2"
              >
                {previewLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Preview
              </button>
            </div>
          </form>
        )}

        {step === 'preview' && preview && (
          <div className="p-6 space-y-4 overflow-y-auto">
            <div className="flex items-center gap-2 text-sm">
              <span
                className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold border ${
                  form.side === 'BUY'
                    ? 'bg-success-50 text-success-700 border-success-100'
                    : 'bg-danger-50 text-danger-700 border-danger-100'
                }`}
              >
                {form.side}
              </span>
              <span className="font-semibold text-ink-900">{form.symbol.toUpperCase()}</span>
              <span className="text-ink-400">·</span>
              <span className="text-ink-600">{form.type === 'LIMIT' ? 'Limit' : 'Market'}</span>
              <span className="text-ink-400">·</span>
              <span className="font-mono tnum text-ink-700">{qty} shares</span>
            </div>

            <div className="rounded-lg bg-ink-50 border border-ink-200 divide-y divide-ink-100 text-sm">
              {[
                ['Customer', orderCustomerId],
                ['Estimated price', `${fmtPrice(preview.estimatedPrice)} ${preview.currencyCode || ''}`.trim()],
                ['Estimated shares', fmtQty(preview.estimatedShares)],
                ['Commission', `${fmtPrice(preview.estimatedCommission)} ${preview.currencyCode || ''}`.trim()],
                ['Fees', `${fmtPrice(preview.estimatedFees)} ${preview.currencyCode || ''}`.trim()],
                ['VAT', `${fmtPrice(preview.estimatedVat)} ${preview.currencyCode || ''}`.trim()],
                ['Estimated total', `${fmtPrice(preview.estimatedTotal)} ${preview.currencyCode || ''}`.trim()],
                ...(form.side === 'BUY' ? [['Available cash', fmtCash(buyingPower.amount, buyingPower.currency)]] : []),
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between gap-4 px-3 py-2">
                  <span className="text-ink-500">{label}</span>
                  <span className="font-mono font-medium text-ink-900 tnum text-right">{val}</span>
                </div>
              ))}
            </div>

            {form.side === 'BUY' && Number(preview.estimatedTotal) > Number(buyingPower.amount) && buyingPower.amount != null && (
              <p className="text-sm text-warning-800 bg-warning-50 border border-warning-100 rounded-lg px-3 py-2">
                Estimated total is above the available cash shown for this customer. The order service will reject the order if buying power is not enough.
              </p>
            )}

            {preview.warnings?.length > 0 && (
              <ul className="space-y-1">
                {preview.warnings.map((w) => (
                  <li key={w} className="text-xs text-warning-700 bg-warning-50 border border-warning-100 rounded px-2.5 py-1.5">
                    {w}
                  </li>
                ))}
              </ul>
            )}

            {placeError && (
              <p className="text-sm text-danger-600 bg-danger-50 border border-danger-100 rounded-lg px-3 py-2">
                {placeError}
              </p>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setStep('form')} className="btn-secondary">
                Back
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                className={`btn-primary ${form.side === 'BUY' ? 'bg-success-600 hover:bg-success-700 border-success-600' : 'bg-danger-600 hover:bg-danger-700 border-danger-600'}`}
              >
                Confirm {form.side === 'BUY' ? 'buy' : 'sell'}
              </button>
            </div>
          </div>
        )}

        {step === 'placing' && (
          <div className="p-6 flex flex-col items-center gap-3 py-10" role="status">
            <Loader2 className="h-8 w-8 animate-spin text-accent-600" />
            <p className="text-sm text-ink-600">Placing {form.side === 'BUY' ? 'buy' : 'sell'} order for {form.symbol.toUpperCase()}…</p>
          </div>
        )}

        {(step === 'success' || step === 'rejected') && (
          <div className="p-6 space-y-4">
            <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${
              step === 'success'
                ? 'bg-success-50 border-success-100 text-success-800'
                : 'bg-danger-50 border-danger-100 text-danger-800'
            }`}>
              {step === 'success'
                ? <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
                : <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />}
              <div>
                <p className="text-sm font-semibold">
                  {step === 'success' ? 'The order was accepted.' : 'The order was not accepted.'}
                </p>
                {step === 'rejected' && placeError && (
                  <p className="text-sm mt-1">{placeError}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-2xs font-semibold border ${
                form.side === 'BUY'
                  ? 'bg-success-50 text-success-700 border-success-100'
                  : 'bg-danger-50 text-danger-700 border-danger-100'
              }`}>
                {form.side}
              </span>
              <span className="text-sm font-semibold text-ink-900">{form.symbol.toUpperCase()}</span>
              <span className="text-sm font-mono tnum text-ink-700">{fmtQty(qty)}</span>
              <Badge tone={statusConfig(placedOrder?.status || (step === 'success' ? 'SUBMITTED' : 'REJECTED')).tone} dot>
                {statusConfig(placedOrder?.status || (step === 'success' ? 'SUBMITTED' : 'REJECTED')).label}
              </Badge>
            </div>
            <dl className="rounded-lg border border-ink-200 divide-y divide-ink-100 text-sm">
              <div className="flex justify-between px-3 py-2">
                <dt className="text-ink-500">Order ID</dt>
                <dd className="font-mono text-ink-900">{placedOrder?.id || '—'}</dd>
              </div>
              <div className="flex justify-between px-3 py-2">
                <dt className="text-ink-500">Status</dt>
                <dd className="font-medium text-ink-900">{placedOrder?.status || (step === 'success' ? 'Submitted' : '—')}</dd>
              </div>
            </dl>
            <div className="flex justify-end gap-2">
              {placedOrder?.id && (
                <Link to={`/orders/${placedOrder.id}`} className="btn btn-sm btn-secondary">
                  View order
                </Link>
              )}
              <button type="button" onClick={onClose} className="btn-primary">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlaceOrderModal;
